"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import Link from "next/link";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import WaveDropTime from "./time/WaveDropTime";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import { useLinkPreviewContext } from "@/components/waves/LinkPreviewContext";
import DropNotFound from "./DropNotFound";
interface WaveDropQuoteProps {
  readonly drop: ApiDrop | null;
  readonly partId: number;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isNotFound?: boolean;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
}
interface WaveDropQuoteProfilePictureProps {
  readonly drop: ApiDrop | null;
}
const WaveDropQuoteProfilePicture: React.FC<WaveDropQuoteProfilePictureProps> = t0 => {
  const $ = _c(7);
  const {
    drop
  } = t0;
  if (!drop) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-h-full tw-w-full tw-max-w-full tw-animate-pulse tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== drop.author.pfp) {
    t1 = drop.author.pfp ? resolveIpfsUrlSync(drop.author.pfp) : null;
    $[1] = drop.author.pfp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const resolvedPfp = t1;
  if (resolvedPfp) {
    const t2 = `${drop.author.handle ?? drop.author.primary_address}'s profile picture`;
    let t3;
    if ($[3] !== resolvedPfp || $[4] !== t2) {
      t3 = <div className="tw-relative tw-h-full tw-w-full"><Image src={resolvedPfp} alt={t2} fill={true} sizes="24px" className="tw-rounded-md tw-bg-transparent tw-object-cover" /></div>;
      $[3] = resolvedPfp;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const WaveDropQuote: React.FC<WaveDropQuoteProps> = t0 => {
  const $ = _c(78);
  const {
    drop,
    partId,
    onQuoteClick,
    isNotFound: t1,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    onLinkCardActionsActiveChange
  } = t0;
  const isNotFound = t1 === undefined ? false : t1;
  const {
    onCardActionsActiveChange
  } = useLinkPreviewContext();
  let t2;
  bb0: {
    if (!drop) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[0] !== drop.parts || $[1] !== partId) {
      t3 = drop.parts.find(part => part.part_id === partId) ?? null;
      $[0] = drop.parts;
      $[1] = partId;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t3;
  }
  const quotedPart = t2;
  let t3;
  if ($[3] !== drop || $[4] !== onQuoteClick) {
    t3 = () => {
      if (drop?.wave.id && drop.id) {
        onQuoteClick(drop);
      }
    };
    $[3] = drop;
    $[4] = onQuoteClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const goToQuoteDrop = t3;
  let t4;
  bb1: {
    if (!drop) {
      t4 = "";
      break bb1;
    }
    const waveDetails = drop.wave as unknown as {
      chat?: {
        scope?: {
          group?: {
            is_direct_message?: boolean | undefined;
          } | undefined;
        } | undefined;
      } | undefined;
    };
    const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
    let t5;
    if ($[6] !== drop.wave.id || $[7] !== isDirectMessage) {
      t5 = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      $[6] = drop.wave.id;
      $[7] = isDirectMessage;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = t5;
  }
  const waveHref = t4;
  let path;
  if ($[9] !== drop || $[10] !== quotePath) {
    path = quotePath ? [...quotePath] : [];
    if (drop?.wave.id) {
      const currentQuoteKey = `${drop.wave.id}:${drop.serial_no}`;
      if (!path.includes(currentQuoteKey)) {
        path.push(currentQuoteKey);
      }
    }
    $[9] = drop;
    $[10] = quotePath;
    $[11] = path;
  } else {
    path = $[11];
  }
  const effectiveQuotePath = path;
  const resolvedOnLinkCardActionsActiveChange = onLinkCardActionsActiveChange ?? onCardActionsActiveChange;
  const isInteractive = drop !== null && !isNotFound;
  let t5;
  if ($[12] !== goToQuoteDrop || $[13] !== isInteractive) {
    t5 = event => {
      event.stopPropagation();
      if (isInteractive) {
        goToQuoteDrop();
      }
    };
    $[12] = goToQuoteDrop;
    $[13] = isInteractive;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleQuoteContainerClick = t5;
  const quoteContainerClassName = `tw-mt-1 ${isInteractive ? "tw-cursor-pointer" : ""} tw-rounded-xl tw-bg-iron-950 tw-px-3 tw-py-3 tw-ring-1 tw-ring-inset tw-ring-iron-800`;
  if (isNotFound) {
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <DropNotFound />;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== handleQuoteContainerClick || $[17] !== quoteContainerClassName) {
      t7 = <div className={quoteContainerClassName} onClick={handleQuoteContainerClick}>{t6}</div>;
      $[16] = handleQuoteContainerClick;
      $[17] = quoteContainerClassName;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  let t6;
  if ($[19] !== goToQuoteDrop || $[20] !== isInteractive) {
    t6 = isInteractive ? e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        goToQuoteDrop();
      }
    } : undefined;
    $[19] = goToQuoteDrop;
    $[20] = isInteractive;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = isInteractive ? "button" : undefined;
  const t8 = isInteractive ? 0 : undefined;
  let t9;
  if ($[22] !== drop) {
    t9 = <div className="tw-relative tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-900"><div className="tw-h-full tw-w-full tw-rounded-md"><WaveDropQuoteProfilePicture drop={drop} /></div></div>;
    $[22] = drop;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== drop) {
    t10 = !!drop && <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[24] = drop;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = `/${drop?.author.handle ?? drop?.author.primary_address}`;
  const t12 = drop?.author.handle ?? drop?.author.primary_address;
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link href={t11} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t12}</Link></p>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className="tw-flex tw-items-center tw-gap-x-2">{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== drop) {
    t15 = !!drop && <><div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" /><WaveDropTime timestamp={drop.created_at} /></>;
    $[32] = drop;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-gap-x-2">{t14}{t15}</div>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== drop || $[38] !== waveHref) {
    t17 = drop && waveHref && <Link href={waveHref} className="tw-leading-0 -tw-mt-1 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    $[37] = drop;
    $[38] = waveHref;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <div>{t17}</div>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = quotedPart?.content ?? "";
  let t20;
  if ($[42] !== drop?.mentioned_users) {
    t20 = drop?.mentioned_users ?? [];
    $[42] = drop?.mentioned_users;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== drop?.mentioned_groups) {
    t21 = drop?.mentioned_groups ?? [];
    $[44] = drop?.mentioned_groups;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== drop?.mentioned_waves) {
    t22 = drop?.mentioned_waves ?? [];
    $[46] = drop?.mentioned_waves;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== drop?.referenced_nfts) {
    t23 = drop?.referenced_nfts ?? [];
    $[48] = drop?.referenced_nfts;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = drop?.nft_links;
  const t25 = drop?.id;
  let t26;
  if ($[50] !== effectiveQuotePath || $[51] !== embedDepth || $[52] !== embedPath || $[53] !== maxEmbedDepth || $[54] !== onQuoteClick || $[55] !== resolvedOnLinkCardActionsActiveChange || $[56] !== t19 || $[57] !== t20 || $[58] !== t21 || $[59] !== t22 || $[60] !== t23 || $[61] !== t24 || $[62] !== t25) {
    t26 = <div className="tw-mt-0.5"><DropPartMarkdownWithPropLogger partContent={t19} mentionedUsers={t20} mentionedGroups={t21} mentionedWaves={t22} referencedNfts={t23} nftLinks={t24} textSize="sm" onQuoteClick={onQuoteClick} currentDropId={t25} embedPath={embedPath} quotePath={effectiveQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} onLinkCardActionsActiveChange={resolvedOnLinkCardActionsActiveChange} /></div>;
    $[50] = effectiveQuotePath;
    $[51] = embedDepth;
    $[52] = embedPath;
    $[53] = maxEmbedDepth;
    $[54] = onQuoteClick;
    $[55] = resolvedOnLinkCardActionsActiveChange;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t16 || $[65] !== t18 || $[66] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-flex-col">{t16}{t18}{t26}</div>;
    $[64] = t16;
    $[65] = t18;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t27 || $[69] !== t9) {
    t28 = <div className="tw-group tw-relative tw-flex tw-w-full tw-flex-col"><div className="tw-flex tw-gap-x-2">{t9}{t27}</div></div>;
    $[68] = t27;
    $[69] = t9;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== handleQuoteContainerClick || $[72] !== quoteContainerClassName || $[73] !== t28 || $[74] !== t6 || $[75] !== t7 || $[76] !== t8) {
    t29 = <div className={quoteContainerClassName} onClick={handleQuoteContainerClick} onKeyDown={t6} role={t7} tabIndex={t8}>{t28}</div>;
    $[71] = handleQuoteContainerClick;
    $[72] = quoteContainerClassName;
    $[73] = t28;
    $[74] = t6;
    $[75] = t7;
    $[76] = t8;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  return t29;
};
export default WaveDropQuote;